<template>
  <ErrorDialog
    v-model="showErrorDialog"
    :message="errorDialogMessage"
  />

  <div class="row justify-content-center">
    <div class="col-md-6 col-lg-5">
      <div class="activate-card">
        <!-- Before Activation: User Information and Password Form -->
        <div v-if="user && !activated" class="form-section">
          <h1 class="page-title">{{ $t('auth.activate.title') }}</h1>

          <p class="description">
            {{ $t('auth.activate.description') }}
          </p>

          <form @submit.prevent="submit" novalidate>
            <div class="form-group">
              <label for="email" class="form-label">{{ $t('auth.forgotPassword.email') }}</label>
              <input
                type="text"
                id="email"
                class="form-control"
                v-model="user.email"
                readonly
              >
            </div>

            <div class="form-group">
              <label for="password" class="form-label">{{ $t('auth.activate.password') }}</label>
              <input
                type="password"
                id="password"
                class="form-control"
                :class="{'is-invalid': errors.password}"
                v-model="password"
                :placeholder="$t('auth.activate.password')"
                :disabled="processing"
                required
              >
              <div v-if="errors.password" class="invalid-feedback">
                {{ errors.password }}
              </div>
            </div>

            <div class="form-group">
              <label for="passwordConfirm" class="form-label">{{ $t('auth.activate.passwordConfirm') }}</label>
              <input
                type="password"
                id="passwordConfirm"
                class="form-control"
                :class="{'is-invalid': errors.passwordConfirm}"
                v-model="passwordConfirm"
                :placeholder="$t('auth.activate.passwordConfirm')"
                :disabled="processing"
                required
              >
              <div v-if="errors.passwordConfirm" class="invalid-feedback">
                {{ errors.passwordConfirm }}
              </div>
            </div>

            <button
              type="submit"
              class="btn btn-primary btn-submit"
              :disabled="processing"
            >
              <span v-if="processing" class="spinner-border spinner-border-sm me-2"></span>
              {{ $t('auth.activate.submit') }}
            </button>
          </form>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="text-center">
          <div class="spinner-border" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="mt-3 description">{{ $t('auth.activate.loading') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {onMounted, ref} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import {useI18n} from 'vue-i18n';
import {activateUser, getUserByRegistrationToken} from '../../../api/user/AuthApi';
import useFlashMessage from '../../../composables/useFlashMessage';
import ErrorDialog from '../../../components/common/ErrorDialog.vue';
import { colors } from '../../../../consts/colors.js';
import { validatePasswordComplexity } from '../../../utils/passwordValidator';

const {t: $t} = useI18n();
const { successMessage, errorMessage } = useFlashMessage();

const route = useRoute();
const router = useRouter();

const password = ref();
const passwordConfirm = ref();
const user = ref();
const loading = ref(false);
const activated = ref(false);
const processing = ref(false);
const errors = ref({});
const showErrorDialog = ref(false);
const errorDialogMessage = ref('');

onMounted(async () => {
  const token = route.query.token;
  
  if (!token) {
    errorMessage($t('auth.activate.invalidToken'));
    await router.push({name: 'login'});
    return;
  }

  loading.value = true;
  try {
    user.value = await getUserByRegistrationToken(token);
  } catch (e) {
    if (e.status === 404) {
      errorMessage($t('auth.activate.tokenExpired'));
    } else {
      errorMessage($t('auth.activate.userNotFound'));
    }
    await router.push({name: 'login'});
  }
  loading.value = false;
});

/**
 * Execute activation
 *
 * @returns {Promise<void>}
 */
const submit = async () => {
  const token = route.query.token;
  
  processing.value = true;
  errors.value = {};
  
  // Client-side validation
  if (!validateForm()) {
    processing.value = false;
    return;
  }
  
  try {
    await activateUser(token, password.value);
    activated.value = true;
    successMessage($t('auth.activate.successMessage'));
    await router.push({name: 'home'});

  } catch (err) {
    if (err.response && err.response.data && err.response.data.errors) {
      // Server-side validation errors
      errors.value = err.response.data.errors;
    } else {
      errorDialogMessage.value = err.message || $t('auth.activate.activationFailed');
      showErrorDialog.value = true;
    }
  }
  processing.value = false;
}

const validateForm = () => {
  let isValid = true;
  errors.value = {};

  // Password validations
  if (!password.value || password.value.trim() === '') {
    errors.value.password = $t('auth.activate.validation.passwordRequired');
    isValid = false;
  } else {
    // Validate password complexity
    const complexityResult = validatePasswordComplexity(password.value);
    if (!complexityResult.isValid) {
      // Show the first error
      const firstError = complexityResult.errors[0];
      // Convert password_invalid_characters to invalidCharacters
      const errorSuffix = firstError.replace('password_', '').replace(/_([a-z])/g, (match, p1) => p1.toUpperCase());
      errors.value.password = $t(`validation.password.${errorSuffix}`);
      isValid = false;
    }
  }

  // Password (confirm) validations
  if (!passwordConfirm.value || passwordConfirm.value.trim() === '') {
    errors.value.passwordConfirm = $t('auth.activate.validation.passwordConfirmRequired');
    isValid = false;
  } else if (password.value !== passwordConfirm.value) {
    errors.value.passwordConfirm = $t('auth.activate.validation.passwordMismatch');
    isValid = false;
  }

  return isValid;
}

</script>

<style lang="scss" scoped>
.activate-card {
  background-color: v-bind('colors.white');
  padding: 2.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px v-bind('colors.shadowColor');
}

.page-title {
  color: v-bind('colors.textPrimary');
  font-size: 1.5rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 1.5rem;
}

.description {
  color: v-bind('colors.textPrimary');
  font-size: 14px;
  line-height: 1.6;
  text-align: center;
  margin-bottom: 2rem;

  strong {
    font-weight: 600;
  }
}

.form-group {
  margin-bottom: 1.5rem;

  .form-label {
    color: v-bind('colors.textPrimary');
    font-weight: 500;
    margin-bottom: 0.5rem;
    display: block;
  }

  .form-control {
    width: 100%;
    border: 1px solid v-bind('colors.borderMediumGray');
    border-radius: 4px;
    padding: 0.75rem;
    color: v-bind('colors.textPrimary');

    &:focus {
      border-color: v-bind('colors.btnBgColorPrimary');
      box-shadow: 0 0 0 0.2rem v-bind('colors.shadowPrimary');
      outline: none;
    }

    &.is-invalid {
      border-color: v-bind('colors.error');
    }
  }

  .invalid-feedback {
    color: v-bind('colors.error');
    font-size: 0.875rem;
    margin-top: 0.25rem;
    display: block;
  }
}

.btn {
  width: 100%;
  padding: 0.75rem;
  font-weight: 500;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.btn-submit {
  background-color: v-bind('colors.btnBgColorPrimary');
  border-color: v-bind('colors.btnBgColorPrimary');
  color: v-bind('colors.white');
  margin-bottom: 1rem;
  border: none;

  &:hover:not(:disabled) {
    background-color: v-bind('colors.blue[600]');
    border-color: v-bind('colors.blue[600]');
  }

  &:active:not(:disabled) {
    background-color: v-bind('colors.blue[700]');
  }
}

.btn-back {
  background-color: v-bind('colors.white');
  color: v-bind('colors.btnBgColorPrimary');
  border: 1px solid v-bind('colors.borderMediumGray');

  &:hover:not(:disabled) {
    background-color: v-bind('colors.gray[50]');
  }

  &:active:not(:disabled) {
    background-color: v-bind('colors.gray[300]');
  }
}
</style>